import React, { useCallback, useEffect, useState } from 'react';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import IHotel from '../../../main/interfaces/IHotel';
import IRoomType from '../../../main/interfaces/IRoomType';
import { useTranslation } from 'react-i18next';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import useGetUser from '../../../main/hooks/useGetUser';
import BookingPublic from '../BookingPublic/BookingPublic';
import '../Hotel/Hotel.css';
import '../Reservations/Reservations.css';

interface IRoomAvailability {
  roomId: number;
  roomNumber: string;
  roomTypeId: number;
  roomTypeName: string;
  status?: number;
  pricePerNight?: number | null;
  totalPrice?: number | null;
  recommendedRatePlanId?: number | null;
  recommendedRatePlanName?: string | null;
}

interface Props {
  hotelId: number | null;
  hotels: IHotel[];
  onHotelChange: (hotelId: number | null) => void;
  onDatesChange: (checkInDate: string, checkOutDate: string) => void;
  refreshKey: number;

  onRoomSelect?: (room: IRoomAvailability, checkInDate: string, checkOutDate: string) => void;
}

const RoomAvailability = ({ hotelId, hotels, onHotelChange, onDatesChange, refreshKey, onRoomSelect }: Props) => {
  const { i18n } = useTranslation();
  const user = useGetUser();

  const [rooms, setRooms] = useState<IRoomAvailability[]>([]);
  const [roomTypes, setRoomTypes] = useState<IRoomType[]>([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedRoom, setSelectedRoom] = useState<{
    room: IRoomAvailability;
    checkInDate: string;
    checkOutDate: string;
  } | null>(null);
  const roomStatuses: Record<number, string> = {
    1: i18n.t('room.available', { defaultValue: 'Available' }),
    2: i18n.t('room.occupied', { defaultValue: 'Occupied' }),
    3: i18n.t('room.cleaning', { defaultValue: 'Cleaning' }),
    4: i18n.t('room.maintenance', { defaultValue: 'Maintenance' }),
  };

  // ================= INIT DATES =================
  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const format = (d: Date) => d.toISOString().split('T')[0];

    setCheckInDate(format(today));
    setCheckOutDate(format(tomorrow));
  }, []);

  // ================= LOAD ROOM TYPES =================
  useEffect(() => {
    const load = async () => {
      if (!hotelId) return;

      try {
        const res = await axiosInstance.get(ApiConstants.hotel.roomTypes);
        setRoomTypes((res.data || []).filter((x: IRoomType) => x.hotelId === hotelId));
      } catch {
        toast.error(i18n.t('reservationDesk.failedToLoadRoomTypes'));
      }
    };

    load();
  }, [hotelId]);

  // ================= SEARCH =================
  const searchRooms = useCallback(async () => {
    if (!hotelId) return;

    if (!checkInDate || !checkOutDate) {
      toast.warning(i18n.t('reservationDesk.selectDatesFirst'));
      return;
    }

    try {
      const [availabilityResponse, roomsResponse] = await Promise.all([
        axiosInstance.post(
          ApiConstants.reservation.availability,
          {
            hotelId,
            checkInDate,
            checkOutDate,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: false,
          },
        ),
        axiosInstance.get(ApiConstants.hotel.rooms),
      ]);

      const roomLookup = new Map(
        (Array.isArray(roomsResponse.data) ? roomsResponse.data : [])
          .filter((room: any) => Number(room.hotelId ?? room.HotelId ?? 0) === Number(hotelId))
          .map((room: any) => [
            Number(room.id ?? room.Id ?? 0),
            {
              roomNumber: room.roomNumber ?? room.RoomNumber,
              status: Number(room.status ?? room.Status ?? 1),
            },
          ]),
      );

      let data = (availabilityResponse.data?.items || availabilityResponse.data || []).map((room: any) => {
        const roomId = Number(room.roomId ?? room.RoomId ?? 0);
        const linkedRoom = roomLookup.get(roomId);

        return {
          ...room,
          roomId,
          roomNumber: room.roomNumber ?? room.RoomNumber ?? linkedRoom?.roomNumber,
          status: linkedRoom?.status ?? Number(room.status ?? room.Status ?? 1),
        };
      });

      if (selectedRoomTypeId) {
        data = data.filter((r: any) => Number(r.roomTypeId) === Number(selectedRoomTypeId));
      }

      setRooms(data);
      setHasSearched(true);
      onDatesChange(checkInDate, checkOutDate);
    } catch (err: any) {
      console.error('AVAILABILITY ERROR:', err?.response?.data || err.message);
      toast.error(i18n.t('reservationDesk.failedToLoadAvailability'));
    }
  }, [hotelId, checkInDate, checkOutDate, selectedRoomTypeId]);

  useEffect(() => {
    if (hotelId) searchRooms();
  }, [hotelId, refreshKey, selectedRoomTypeId]);

  // ================= UI =================
  return (
    <div className="hotel-search">
      <div className="reservation-search-shell">
        <div className="hotel-search-bar reservation-search-bar">
          <div className="hotel-input reservation-input-card reservation-hotel-card">
            <label>{i18n.t('reservationDesk.hotel')}</label>
            <select value={hotelId ?? ''} onChange={(e) => onHotelChange(e.target.value ? Number(e.target.value) : null)}>
              <option value="">{i18n.t('reservationDesk.selectHotel')}</option>
              {hotels.map((hotel) => (
                <option key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </option>
              ))}
            </select>
          </div>

          <div className="hotel-input reservation-input-card">
            <label>{i18n.t('reservationDesk.checkIn')}</label>
            <input type="date" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} />
          </div>

          <div className="hotel-input reservation-input-card">
            <label>{i18n.t('reservationDesk.checkOut')}</label>
            <input type="date" value={checkOutDate} onChange={(e) => setCheckOutDate(e.target.value)} />
          </div>

          <div className="hotel-input reservation-input-card reservation-roomtype-card">
            <label>{i18n.t('reservationDesk.roomType')}</label>
            <select value={selectedRoomTypeId} onChange={(e) => setSelectedRoomTypeId(e.target.value)}>
              <option value="">{i18n.t('reservationDesk.allRoomTypes')}</option>
              {roomTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="reservation-search-actions">
            <button className="hotel-search-btn reservation-search-cta" onClick={searchRooms}>
              {i18n.t('reservationDesk.searchRooms')}
            </button>
          </div>
        </div>
      </div>

      {hasSearched && rooms.length === 0 && <div className="hotel-empty">{i18n.t('reservationDesk.noAvailableRooms')}</div>}

      <div className="rooms-grid">
        {rooms.map((room) => (
          <div key={room.roomId} className={`room-card room-card-status-${room.status ?? 1}`}>
            <div className="room-card-shell">
              <div className="room-card-visual" aria-hidden="true">
                <>
                  <CottageOutlinedIcon className="room-card-home-icon" />
                  <MeetingRoomOutlinedIcon className="room-card-door-icon" />
                </>
              </div>

              <div className="room-card-content">
                <div className="room-header">
                  <div className="room-header-top">
                    <span className={`room-availability-pill room-availability-pill-${room.status ?? 1}`}>
                      {roomStatuses[room.status ?? 1] ?? i18n.t('generic.available')}
                    </span>
                    <span className="room-id-pill">ID {room.roomId}</span>
                  </div>
                  <h3>{i18n.t('reservationDesk.roomNumber', { roomNumber: room.roomNumber })}</h3>
                  <span>{room.roomTypeName}</span>
                </div>

                <div className="room-meta-list">
                  <div className="room-meta-row">
                    <span className="room-meta-label" aria-label={i18n.t('generic.status')}>
                      <CheckCircleOutlineOutlinedIcon fontSize="inherit" />
                    </span>
                    <strong>{roomStatuses[room.status ?? 1] ?? i18n.t('generic.available')}</strong>
                  </div>

                  <div className="room-meta-row">
                    <span className="room-meta-label" aria-label={i18n.t('reservationDesk.room')}>
                      <MeetingRoomOutlinedIcon fontSize="inherit" />
                    </span>
                    <strong>{room.roomNumber}</strong>
                  </div>

                  <div className="room-meta-row">
                    <span className="room-meta-label room-meta-currency">
                      {user?.companyCurrencySymbol ?? ''}
                    </span>
                    <strong>
                      {room.totalPrice ?? room.pricePerNight ?? 0}
                      {user?.companyCurrencySymbol ? ` ${user.companyCurrencySymbol}` : ''}
                    </strong>
                  </div>
                </div>

                <div className="room-meta">
                  {room.pricePerNight ? <span>{room.pricePerNight} / {i18n.t('reservationDesk.perNight')}</span> : null}
                  {room.totalPrice ? <span>{i18n.t('generic.total')}: {room.totalPrice}</span> : null}
                </div>

                <div className="room-card-actions">
                  <button
                    className="reserve-btn"
                    disabled={(room.status ?? 1) !== 1}
                    onClick={() => {
                      if ((room.status ?? 1) !== 1) {
                        return;
                      }

                      if (onRoomSelect) {
                        onRoomSelect(room, checkInDate, checkOutDate);
                      } else {
                        setSelectedRoom({
                          room,
                          checkInDate,
                          checkOutDate,
                        });
                      }
                    }}
                  >
                    {i18n.t('reservationDesk.requestBooking')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ================= MODAL ================= */}
      {selectedRoom && (
        <GeneralModal
          handleClose={() => setSelectedRoom(null)}
          headerName={i18n.t('reservationDesk.sendBookingRequest')}
          width="980px"
          radius="16px"
          hideFooter
        >
          <BookingPublic
            hotelId={hotelId}
            room={selectedRoom.room}
            checkInDate={selectedRoom.checkInDate}
            checkOutDate={selectedRoom.checkOutDate}
            onClose={() => setSelectedRoom(null)}
          />
        </GeneralModal>
      )}
    </div>
  );
};

export default RoomAvailability;
